import Vue from "vue";
import Router from "vue-router";
import moment from "moment-timezone";

// Separate route imports
import OMNIARoutes from "./routes/omnia-routes";
import UserRoutes from "./routes/user-routes";
import PhoneSystemRoutes from "./routes/phone-system-routes";
import AccountRoutes from "./routes/account-routes";
import BillingRoutes from "./routes/billing-routes";
import ReportsRoutes from "./routes/reports-routes";
import SystemAdminRoutes from "./routes/system-admin-routes";
import PortRequestsRoutes from "./routes/port-requests-routes";

//apps
import Dashboard from "../views/pages/dashboard/view.vue";

//feathers client
import feathersClient from "../feathers/feathers-client";

//ui
import layouts from "../layout";
import store from "../vuex/store";

Vue.use(Router);

const router = new Router({
  mode: "history",
  routes: [
    ...UserRoutes,
    ...PhoneSystemRoutes,
    ...AccountRoutes,
    ...BillingRoutes,
    ...ReportsRoutes,
    ...SystemAdminRoutes,
    ...OMNIARoutes,
    ...PortRequestsRoutes,
    {
      path: "/",
      alias: "/dashboard",
      name: "dashboard",
      component: Dashboard,
      meta: {
        auth: true,
        layout: layouts.navLeft,
        searchable: true,
        tags: ["app"],
        roles: [1, 3, 9, 11, 12],
        reporting: true
      }
    },
    {
      path: "/logout",
      beforeEnter(to, from, next) {
        auth.logout();

				// ⭐️ `USER LOGOUT` user event log ⭐️ //
				store.dispatch('userEvents/create', { event: "Logout" })

        next({
          path: "/login"
        });
      }
    }
  ]
});

const l = {
  contenOnly() {
    store.commit("setLayout", layouts.contenOnly);
  },
  navLeft() {
    store.commit("setLayout", layouts.navLeft);
  },
  navRight() {
    store.commit("setLayout", layouts.navRight);
  },
  navTop() {
    store.commit("setLayout", layouts.navTop);
  },
  navBottom() {
    store.commit("setLayout", layouts.navBottom);
  },
  set(layout) {
    store.commit("setLayout", layout);
  }
};

const auth = {
  loggedIn() {
    return store.getters.isLogged;
  },
  logout() {
    store.commit("setLogout");
    localStorage.removeItem("feathers-jwt");
    window.location.href = "/login";
  }
};

// disable the NavigationDuplicate error happening in the console
const beforePush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return beforePush.call(this, location).catch(error => error);
};

router.beforeEach(async (to, from, next) => {

  let authrequired = to && to.meta && to.meta.auth;

  // set the layout for the route we are going to.
  if (to && to.meta && to.meta.layout) {
    l.set(to.meta.layout);
  }

  //set up the branding here if it hasn't been already.
  const branding = store.state.branding;
  if (!Object.keys(branding).length) {
    const domain = window.location.hostname;
    await feathersClient
      .service("omnia-branding")
      .find({
        query: {
          domain
        }
      })
      .then(res => {
        store.commit("setBranding", res);
      });
  }

  if (authrequired) {
    if (auth.loggedIn()) {

      //auth against the existing jwt
      if (!store.state.auth.user) {
        store
          .dispatch("auth/authenticate")
          .then(async () => {
            //it might be a good idea to move this logic to where it renders (toolbar). I am undecided.
            const user = store.state.auth.user;
            let {
              myExtension,
              timezone,
              tenantId: userTenantId,
              userRole,
              partnerId
            } = user;


            if(!myExtension){
              this.$notify({
                title: "No Extension",
                message: "Your user has no assigned extension. Contact an admin.",
                type: "error",
                duration: 0,
              });

              await new Promise((resolve) => setTimeout(resolve, 100));

              router.push('logout');
            }

            const offsetUTCServer = moment.tz("America/Chicago").utcOffset();
            const offsetMe = moment.tz(timezone).utcOffset();
            const userOffset = offsetMe - offsetUTCServer;
            store.commit("setUserTimeOffset", userOffset);

            //if this is a platform admin or partner admin hook to accounts channel real time
            if (+userRole === 1 || +userRole === 11) {
              await feathersClient.service("channel-memberships").create({
                roomType:
                  +userRole === 1 ? "platform-accounts" : "partner-accounts",
                partnerId
              });
            }

            if (myExtension) {
              const {
                peerName,
                tenantCode,
                hostServer,
                number,
                tenantId,
                omniaPhonebookId,
                dnd,
                status
              } = user.myExtension;

              //update user specific info in store
              store.commit("setUserDND", dnd === "on");
              // store.commit('setUserStatus', status);
              store.commit("setUserHostServer", hostServer);

              //joins the user specific channel
              await feathersClient.service("channel-memberships").create({
                roomType: "user",
                peerName
              });

              //send request to get any connected calls if not landing on page that already calls it
              if (to.name !== "OMNIA Queues" && peerName) {
                feathersClient.service("nats-actions-handler").create({
                  reqType: "refreshConnectedCalls",
                  hostServer,
                  peerName,
                  tenantCode,
                  tenantId,
                  userId: user.id,
                  userExten: number,
                  peerNames: [peerName]
                });
              }

              // Load custom contacts
              if (omniaPhonebookId) {
                await store.dispatch("phonebook-entries/find", {
                  query: {
                    tenantId,
                    phonebookId: omniaPhonebookId
                  }
                });
              }
            }
          })
          .then(() => {
            const {userRole, enableReporting} = store.state.auth.user;
            if (!to.meta.hasOwnProperty("roles")) {
              next();
            } else {
              const roleArray = to.meta.roles;

              // blocked by user role but reporting admin features are extended to user
              if(!roleArray.includes(userRole) && to.meta.reporting && enableReporting) {
                next();
                return;
              }

              if (roleArray.includes(userRole)) {
                next();
                return;
              }

              router.push("/omnia/contacts");

            }

            // ⭐️ `Page Refresh / Persisted Auth` user event log ⭐️ //
            store.dispatch('userEvents/create', { event: "App Connect" })
            store.dispatch("promptCurrentLocation");
          })
          .catch(err => {
            localStorage.removeItem("feathers-jwt");
            store.commit('setLogout');
            if (to.name !== "login") {

              //set the intended route
              const originalRoute = {...to};
              store.commit('setOriginalRoute', to.path);
              router.push('/login');
              return false;
            }
            router.push('/login');
          });
      } else {

        if (to.name === "login") {
          router.push('/')
          // window.location.href = "/";
          return false;
        } else {
          const { userRole, enableReporting, enabledOperatorPanel } = store.state.auth.user;

          if (!to.meta.hasOwnProperty("roles")) {
            next();
          } else {
            const roleArray = to.meta.roles;

            // blocked by user role but reporting admin features are extended to user
            if(!roleArray.includes(userRole) && to.meta.reporting && enableReporting){
              next();
              return false;
            }

            if (roleArray.includes(userRole)) {
              next();
              return false;
            }

            router.push("/omnia/contacts");

          }
        }
      }
    } else {

      if (to.name !== "login") {

        //save the intended route
        const origRoute = {...to};
        store.commit('setOriginalRoute', origRoute.path);
        router.push('/login');
        return false;
      }

      router.push('/login');
    }
  } else {
    // an inbound SSO completion must reach Login.vue even when a stale jwt still
    // looks "logged in"
    const isSSOReturn = !!(to.query && (to.query.accessToken || to.query.error));

    if (auth.loggedIn() && to.name === "login" && !isSSOReturn) {
      router.push('/omnia/contacts');
      return false;
    } else {
      next();
    }
  }
});

router.afterEach((to, from) => {
  setTimeout(() => {
    store.commit("setSplashScreen", false);
  }, 500);
});

export default router;
